import { useCallback, useEffect, useState } from "react";
import { Tabs, Button, Modal, Row, message, Spin, Avatar } from "antd";
import dayjs from "dayjs";
import {
  cancelCollabInvite,
  fetchUserActivities,
  reviewBusinessApplication,
} from "services/SauceService";
import { InviteStatusEnum } from "interfaces/creator";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCheck,
  faCalendarAlt,
  faPaperPlane,
  faFileAlt,
  faEye,
  faEnvelope,
} from "@fortawesome/free-solid-svg-icons";
import RescheduleRequest from "components/reschedule-request/RescheduleRequest";
import { useLocation, useNavigate } from "react-router-dom";
import ModalCollabTimeSlot from "pages/influencers/ModalCollabTimeSlot";
import { motion } from "framer-motion";
import { formatNumberKMBT } from "pages/influencers/InfluencerCard";
import "./PendingActivities.scss";
import { parseHourTo24 } from "pages/applications/Applications";
import { Venue } from "interfaces/venue";

const { TabPane } = Tabs;

export interface AddressCoordinates {
  type: "Point";
  coordinates: number[]; // [lng, lat]
}

export interface VenueWorkingHour {
  _id?: string;
  dayOfWeek?: string;
  startTime?: string;
  endTime?: string;
  open?: boolean;
}

export interface RequestDate {
  _id?: string;
  date?: string | null;
  hour?: string | null;
}

export interface ApplicationDate {
  hour?: string | null;
  date?: string | null;
  // some items have an empty object for applicationDate, keep optional props
}

export interface PendingActivity {
  collabId?: string | null;
  bookingId?: string | null;
  inviteId?: string | null;
  inviteStatus?: InviteStatusEnum | null;
  venue: Venue;
  startDate?: string | null;
  endDate?: string | null;
  status?: number | null;
  bookingStatus?: number | null;
  foodieId?: string;
  username?: string;
  instagramFollowersCount?: number;
  tiktokFollowersCount?: number;
  collabMatchRate?: number | null;
  selfManagedCompensationEnabled?: boolean;
  deliveryTeamApproval?: boolean;
  avatarUrl?: string | null;
  isPremium?: boolean;
  top3PostsCoverImgUrl?: string[];
  dinnerIntentByPosts?: any;
  commitmentRate?: number;
  engagementRate?: number | null;
  deliveryAddress?: string | null;
  requestDates?: RequestDate[];
  applicationDate?: ApplicationDate;
  dueDateToReview?: string | null;
  tabStatus?: string;
  securityCode?: string | null;
  foodCompAmount?: number | null;
  cashCompAmount?: number | null;
  guestAllowed?: boolean;
  name?: string;
  verified?: boolean;
  requestedAt?: string | null;
  // allow additional unknown keys returned by API
  [key: string]: any;
}

interface PendingActivitiesProps {
  creator?: PendingActivity;
  showCreatorInfo?: boolean;
}

// framer-motion variants
const listVariants = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.06,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 8 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.5 },
  },
};

const formatLeft = (details: string) => {
  return details || "";
};

const computeTimeLeft = (expiresAt?: string | number) => {
  if (!expiresAt) return "";
  const now = dayjs();
  const end = dayjs(expiresAt);
  if (!end.isValid() || end.isBefore(now)) return "Expiring soon";
  const diff = end.diff(now);
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  if (days > 0) return `${days}d ${hours}h left`;
  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
  if (hours > 0) return `${hours}h ${minutes}m left`;
  return `${minutes}m left`;
};
const getBookingTimestamp = (item: any) => {
  const date = item?.applicationDate?.date;
  const hour = item?.applicationDate?.hour;
  if (!date) return 0;

  const timeStr = parseHourTo24(hour);
  const datePart = date.includes("T") ? date.split("T")[0] : date;

  const dateTimeStr = hour ? `${datePart}T${timeStr}Z` : date;
  const ts = new Date(dateTimeStr).getTime();
  return isNaN(ts) ? 0 : ts;
};

const PendingActivities: React.FC<PendingActivitiesProps> = ({
  creator,
  showCreatorInfo,
}) => {
  const navigate = useNavigate();
  const location = useLocation();

  const [activities, setActivities] = useState<any>({
    bookings: [],
    applications: [],
    invitations: [],
    content: [],
  });

  // state to control the time-slot modal and the influencer currently being acted on
  const [openModalTimeSlot, setOpenModalTimeSlot] = useState(false);
  const [selectedInfluencer, setSelectedInfluencer] = useState<any>(null);
  // loading state for activities
  const [loading, setLoading] = useState<boolean>(false);

  const bookings = activities.bookings || [];
  const applications = activities.applications || [];
  const invitations = activities.invitations || [];
  // const content = activities.content || [];

  const canRescheduleAccepted = (influencer: any) => {
    if (!influencer) return false;
    const status = String(
      influencer?.tabStatus ?? influencer?.TabStatus ?? ""
    ).toLowerCase();
    if (status !== "accepted") return false;

    const app = influencer?.applicationDate;
    if (!app) return false;

    const baseDate = app.date ? dayjs(app.date) : null;
    if (!baseDate || !baseDate.isValid()) return false;

    let scheduled = baseDate;
    if (app.hour) {
      const parsedTime = dayjs(app.hour, "h:mm A");
      if (parsedTime.isValid()) {
        scheduled = scheduled
          .hour(parsedTime.hour())
          .minute(parsedTime.minute())
          .second(0)
          .millisecond(0);
      }
    }

    return scheduled.isAfter(dayjs().add(3, "hour"));
  };

  // const onClickMessage = async (collab: any) => {
  //   const { collabId, bookingId } = collab;
  //   const response: any = await collabStartChat({ collabId, bookingId });
  //   if (response?.chat?._id) navigate(`/messenger/${response.chat._id}`);
  // };

  const onClickCancelInvite = (invite: PendingActivity) => {
    Modal.confirm({
      content: "Are you sure you want to cancel this invite?",
      icon: "",
      onOk: async () => {
        const params = {
          inviteId: invite.inviteId,
        };
        await cancelCollabInvite(params);
        message.success("Invite cancelled");
      },
      okButtonProps: {
        type: "primary",
      },
    });
  };

  const onClickAccept = (evt: any, influencer: any) => {
    // prevent propagation immediately
    evt.stopPropagation();

    // remember which influencer triggered the action
    setSelectedInfluencer(influencer);

    const bookDate = influencer?.formatDate
      ? dayjs.utc(influencer?.formatDate.date || "").format("DD MMM YYYY")
      : influencer?.requestDates && influencer?.requestDates.length > 0
      ? dayjs.utc(influencer?.requestDates[0]?.date || "").format("DD MMM YYYY")
      : "";

    const bookTime = influencer?.formatDate
      ? influencer?.formatDate?.hour
      : influencer?.requestDates && influencer?.requestDates.length > 0
      ? influencer?.requestDates[0]?.hour
      : "";
    if (influencer?.venue?.ghostKitchen) {
      Modal.confirm({
        content: (
          <>
            <Row>
              <span>
                <b>Delivery address:</b> {influencer?.deliveryAddress}
              </span>
            </Row>
            <Row>
              <span>
                <b>Date:</b> {`${bookDate} - ${bookTime}`}
              </span>
            </Row>
          </>
        ),
        icon: "",
        title: "Are you sure you want Accept this collab?",
        onOk: async () => {
          try {
            await reviewBusinessApplication({
              bookingId: influencer?.bookingId,
              collabId: influencer?.collabId,
              slotTimeId: influencer?.requestDates[0]?._id,
              status: "approve",
            });

            navigate(location.pathname, { replace: true });
          } catch {
            message.error("Error accepting collab");
          }
        },
        okButtonProps: {
          type: "primary",
        },
      });
    } else {
      setOpenModalTimeSlot(true);
    }
  };

  const onClickReject = async (evt: any, influencer: any) => {
    evt.stopPropagation();

    Modal.confirm({
      content: "Are you sure you want to reject this influencer?",
      icon: "",
      onOk: async () => {
        await reviewBusinessApplication({
          bookingId: influencer?.bookingId,
          collabId: influencer?.collabId,
          status: "reject",
        });

        navigate(location.pathname, { replace: true });
      },
      okButtonProps: {
        type: "primary",
      },
    });
  };

  const getCreatorActivities = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        businessId: localStorage.getItem("businessId") || "",
        foodieId: creator?.foodieId,
      };

      const response = await fetchUserActivities(params);

      const list = Array.isArray(response?.data)
        ? response.data
        : Array.isArray(response)
        ? response
        : [];
      const grouped = {
        bookings: [] as any[],
        applications: [] as any[],
        invitations: [] as any[],
        content: [] as any[],
      };

      list.forEach((item: any) => {
        const raw = item.TabStatus ?? item.tabStatus ?? item.tabstatus ?? "";
        const status = String(raw).toLowerCase().trim();

        if (status === "accepted") {
          grouped.bookings.push(item);
        } else if (status === "pending") {
          grouped.applications.push(item);
        } else if (status === "invited") {
          grouped.invitations.push(item);
        } else if (status === "content") {
          grouped.content.push(item);
        } else {
          if (item.type === "booking") grouped.bookings.push(item);
          else if (item.type === "content") grouped.content.push(item);
          else grouped.applications.push(item);
        }
      });

      grouped.bookings.sort(
        (a: any, b: any) => getBookingTimestamp(a) - getBookingTimestamp(b)
      );

      grouped.applications.sort(
        (a: any, b: any) =>
          new Date(a.requestedAt).getTime() - new Date(b.requestedAt).getTime()
      );

      setActivities(grouped);
    } catch (err) {
      console.error("Failed to organize activities by TabStatus", err);
    } finally {
      setLoading(false);
    }
  }, [creator?.foodieId]);

  useEffect(() => {
    getCreatorActivities();
  }, [getCreatorActivities]);

  return (
    <div className="pending-activities-wrapper">
      <ModalCollabTimeSlot
        open={openModalTimeSlot}
        onCancel={() => {
          setOpenModalTimeSlot(false);
          setSelectedInfluencer(null);
        }}
        requestDates={selectedInfluencer?.requestDates}
        influencer={selectedInfluencer}
      />

      <div className="pa-card" style={{ position: "relative" }}>
        {/* loading overlay */}
        {loading && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.18 }}
            style={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: "rgba(255,255,255,0.8)",
              zIndex: 20,
              height: 350,
            }}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.24 }}
            >
              <Spin size="large" />
            </motion.div>
          </motion.div>
        )}

        <Tabs defaultActiveKey="upcoming">
          <TabPane tab="Bookings" key="upcoming">
            <motion.div
              className="activity-list bookings-list"
              variants={listVariants}
              initial="hidden"
              animate="visible"
            >
              {bookings.length === 0 && !loading ? (
                <div
                  className="empty-bookings"
                  style={{ textAlign: "center", padding: "40px 20px" }}
                >
                  <div
                    style={{
                      width: 76,
                      height: 76,
                      borderRadius: 38,
                      background: "#f3f4f6",
                      margin: "0 auto 16px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <FontAwesomeIcon
                      icon={faCalendarAlt}
                      style={{ fontSize: 28, color: "#9aa0a6" }}
                    />
                  </div>

                  <div
                    style={{ fontSize: 20, fontWeight: 700, marginBottom: 8 }}
                  >
                    No Upcoming Bookings
                  </div>

                  <div
                    style={{
                      color: "#9aa0a6",
                      maxWidth: 560,
                      margin: "0 auto 16px",
                      display: "none", //"block",
                    }}
                  >
                    This creator doesn't have any confirmed bookings yet. Once
                    they accept invitations or you confirm their applications,
                    they'll appear here.
                  </div>

                  <div
                    style={{
                      display: "none", //"flex",
                      gap: 12,
                      justifyContent: "center",
                    }}
                  >
                    <Button
                      type="primary"
                      onClick={() => {
                        /* implement send invitation flow if needed */
                      }}
                    >
                      <FontAwesomeIcon
                        icon={faPaperPlane}
                        style={{ marginRight: 8 }}
                      />
                      Send Invitation
                    </Button>

                    <Button
                      onClick={() => {
                        /* implement add to campaign flow if needed */
                      }}
                    >
                      + Add to Campaign
                    </Button>
                  </div>
                </div>
              ) : (
                bookings.map((a: any) => (
                  <motion.div
                    className="booking-row"
                    key={a.id}
                    variants={itemVariants}
                  >
                    <div className="booking-left">
                      {showCreatorInfo ? (
                        <>
                          <div className="creator-block">
                            <Avatar
                              src={a.avatarUrl}
                              alt={a.name || "creator"}
                              className="creator-avatar"
                            />

                            <div className="creator-meta">
                              <div className="creator-name">
                                {a.name || "Creator"}
                              </div>
                              <div className="creator-handle">
                                @{a.username || "@something"}
                                {` • ${formatNumberKMBT(
                                  (a.tiktokFollowersCount || 0) +
                                    (a.instagramFollowersCount || 0),
                                  0
                                )}`}
                              </div>

                              {/* <div className="creator-badges">
                                {a.verified && (
                                  <span className="verified-pill">Verified</span>
                                )}
                              </div> */}
                            </div>
                          </div>
                          <div
                            className="booking-divider"
                            aria-hidden="true"
                            style={{
                              width: 1,
                              backgroundColor: "rgba(0,0,0,0.08)",
                              margin: "12px 16px",
                              alignSelf: "stretch",
                            }}
                          />
                        </>
                      ) : (
                        <div className="booking-icon-box">
                          <FontAwesomeIcon
                            icon={faEnvelope}
                            className="booking-icon"
                          />
                        </div>
                      )}

                      <div className="booking-info">
                        <div className="booking-title">{a?.venue?.name}</div>
                        <div className="booking-address">
                          {a?.venue?.addressDisplay}
                        </div>
                        {a.securityCode && (
                          <div className="booking-comp">
                            Voucher: {a.securityCode} - ${a?.foodCompAmount}
                            {" credit "}
                            {a?.guestAllowed && " ∘ 1 guest"}
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="booking-right">
                      <div className="booking-datetime">
                        <div className="booking-date">
                          {a.applicationDate
                            ? dayjs
                                .utc(a.applicationDate?.date)
                                .format("MMMM D, YYYY")
                            : formatLeft(a.details)}
                        </div>
                        <div className="booking-time">
                          {a.applicationDate?.hour || "-"}
                        </div>
                      </div>

                      <div className="booking-actions">
                        {canRescheduleAccepted(a) && (
                          <RescheduleRequest asButton influencer={a} />
                        )}
                        {/* <motion.div
                          style={{ display: "inline-block" }}
                          whileTap={{ scale: 0.95 }}
                        >
                          <Button className="red-hover">Cancel</Button>
                        </motion.div> */}
                      </div>
                    </div>
                  </motion.div>
                ))
              )}
            </motion.div>
          </TabPane>

          {/* Applications tab rewritten to match screenshot layout */}
          <TabPane tab="Applications" key="applications">
            <motion.div
              className="activity-list bookings-list"
              variants={listVariants}
              initial="hidden"
              animate="visible"
            >
              {applications.length === 0 ? (
                <div style={{ textAlign: "center", padding: "40px 20px" }}>
                  <div
                    style={{
                      width: 76,
                      height: 76,
                      borderRadius: 38,
                      background: "#f3f4f6",
                      margin: "0 auto 16px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <FontAwesomeIcon
                      icon={faFileAlt}
                      style={{ fontSize: 28, color: "#9aa0a6" }}
                    />
                  </div>

                  <div
                    style={{ fontSize: 20, fontWeight: 700, marginBottom: 8 }}
                  >
                    No Applications Received
                  </div>

                  <div
                    style={{
                      color: "#9aa0a6",
                      maxWidth: 560,
                      margin: "0 auto 16px",
                      display: "none", //"block",
                    }}
                  >
                    This creator hasn't applied to any of your campaigns yet.
                    You can invite them directly to specific campaigns or wait
                    for them to discover and apply to your open collaborations.
                  </div>

                  <div
                    style={{
                      display: "none", //"flex",
                      gap: 12,
                      justifyContent: "center",
                    }}
                  >
                    <Button
                      type="primary"
                      onClick={() => {
                        /* open send campaign invite flow */
                      }}
                    >
                      <FontAwesomeIcon
                        icon={faPaperPlane}
                        style={{ marginRight: 8 }}
                      />
                      Send Campaign Invite
                    </Button>

                    <Button
                      onClick={() => {
                        /* open view campaigns */
                      }}
                    >
                      <FontAwesomeIcon
                        icon={faEye}
                        style={{ marginRight: 8 }}
                      />
                      View Open Campaigns
                    </Button>
                  </div>
                </div>
              ) : (
                applications.map((a: any) => (
                  <motion.div
                    className="booking-row"
                    key={a.id}
                    variants={itemVariants}
                  >
                    <div className="booking-left">
                      {showCreatorInfo ? (
                        <>
                          <div className="creator-block">
                            <Avatar
                              src={a.avatarUrl}
                              alt={a.name || "creator"}
                              className="creator-avatar"
                            />

                            <div className="creator-meta">
                              <div className="creator-name">
                                {a.name || "Creator"}
                              </div>
                              <div className="creator-handle">
                                @{a.username || "@something"}
                                {` • ${formatNumberKMBT(
                                  (a.tiktokFollowersCount || 0) +
                                    (a.instagramFollowersCount || 0),
                                  0
                                )}`}
                              </div>

                              {/* <div className="creator-badges">
                                {a.verified && (
                                  <span className="verified-pill">Verified</span>
                                )}
                              </div> */}
                            </div>
                          </div>
                          <div
                            className="booking-divider"
                            aria-hidden="true"
                            style={{
                              width: 1,
                              backgroundColor: "rgba(0,0,0,0.08)",
                              margin: "12px 16px",
                              alignSelf: "stretch",
                            }}
                          />
                        </>
                      ) : (
                        <div className="booking-icon-box">
                          <FontAwesomeIcon
                            icon={faEnvelope}
                            className="booking-icon"
                          />
                        </div>
                      )}

                      <div className="booking-info">
                        <div className="booking-title">{a?.venue?.name}</div>
                        <div className="booking-address">
                          {a?.venue?.addressDisplay}
                        </div>
                        {(a.foodCompAmount || a.cashCompAmount) && (
                          <div className="booking-comp">
                            {a.foodCompAmount
                              ? `Product Comp: $${a.foodCompAmount}`
                              : null}
                            {a.foodCompAmount && a.cashCompAmount
                              ? " · "
                              : null}
                            {a.cashCompAmount
                              ? `Cash Comp: $${a.cashCompAmount}`
                              : null}
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="booking-right">
                      <div className="booking-datetime">
                        <div className="booking-date">
                          Applied:{" "}
                          <span>
                            {dayjs(a?.requestedAt).format("MMMM D, YYYY")}
                          </span>
                        </div>
                        <div className="application-left-pill">
                          {a.dueDateToReview
                            ? computeTimeLeft(a.dueDateToReview)
                            : a.timeLeft || ""}
                        </div>
                      </div>

                      <div className="booking-actions">
                        {canRescheduleAccepted(a) && (
                          <RescheduleRequest asButton influencer={a} />
                        )}
                        <motion.div
                          style={{ display: "inline-block", marginRight: 8 }}
                          whileTap={{ scale: 0.97 }}
                        >
                          <Button
                            type="primary"
                            onClick={(e) => onClickAccept(e, a)}
                          >
                            <FontAwesomeIcon icon={faCheck} /> Accept
                          </Button>
                        </motion.div>

                        <motion.div
                          style={{ display: "inline-block" }}
                          whileTap={{ scale: 0.97 }}
                        >
                          <Button
                            className="red-hover"
                            onClick={(e) => onClickReject(e, a)}
                          >
                            Decline
                          </Button>
                        </motion.div>
                      </div>
                    </div>
                  </motion.div>
                ))
              )}
            </motion.div>
          </TabPane>

          {/* Invitations tab updated to match screenshot layout */}
          <TabPane tab="Invitations" key="invitations">
            <motion.div
              className="activity-list bookings-list"
              variants={listVariants}
              initial="hidden"
              animate="visible"
            >
              {invitations.map((invite: PendingActivity) => (
                <motion.div
                  className="booking-row"
                  key={invite.id}
                  variants={itemVariants}
                >
                  <div className="booking-left">
                    {showCreatorInfo ? (
                      <>
                        <div className="creator-block">
                          <Avatar
                            src={invite.avatarUrl}
                            alt={invite.name || "creator"}
                            className="creator-avatar"
                          />

                          <div className="creator-meta">
                            <div className="creator-name">
                              {invite.name || "Creator"}
                            </div>
                            <div className="creator-handle">
                              @{invite.username || "@something"}
                              {` • ${formatNumberKMBT(
                                (invite.tiktokFollowersCount || 0) +
                                  (invite.instagramFollowersCount || 0),
                                0
                              )}`}
                            </div>

                            {/* <div className="creator-badges">
                                {a.verified && (
                                  <span className="verified-pill">Verified</span>
                                )}
                              </div> */}
                          </div>
                        </div>
                        <div
                          className="booking-divider"
                          aria-hidden="true"
                          style={{
                            width: 1,
                            backgroundColor: "rgba(0,0,0,0.08)",
                            margin: "12px 16px",
                            alignSelf: "stretch",
                          }}
                        />
                      </>
                    ) : (
                      <div className="booking-icon-box">
                        <FontAwesomeIcon
                          icon={faEnvelope}
                          className="booking-icon"
                        />
                      </div>
                    )}
                    <div className="booking-info">
                      <div className="booking-title">{invite?.venue?.name}</div>
                      <div className="booking-address">
                        {invite?.venue?.addressDisplay}
                      </div>
                      {(invite.foodCompAmount || invite.cashCompAmount) && (
                        <div className="booking-comp">
                          {invite.foodCompAmount
                            ? `Product Comp: $${invite.foodCompAmount}`
                            : null}
                          {invite.foodCompAmount && invite.cashCompAmount
                            ? " · "
                            : null}
                          {invite.cashCompAmount
                            ? `Cash Comp: $${invite.cashCompAmount}`
                            : null}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="booking-right">
                    <div className="booking-datetime">
                      <div className="booking-date">
                        Invited:{" "}
                        {invite.requestDates?.length === 0 ? (
                          <>Regular working hours</>
                        ) : (
                          invite.requestDates?.map(
                            (fullDate: any, index: number) => (
                              <span
                                key={index}
                                style={{
                                  display: index === 0 ? "inline" : "block",
                                }}
                              >
                                {" "}
                                {dayjs(fullDate?.date).format("MMMM D, YYYY")}
                              </span>
                            )
                          )
                        )}
                      </div>
                      <div className="application-left-pill">
                        {invite.dueDateToReview
                          ? computeTimeLeft(invite.dueDateToReview)
                          : invite.timeLeft || ""}
                      </div>
                    </div>

                    {invite.inviteStatus === InviteStatusEnum.Pending && (
                      <div className="booking-actions">
                        <motion.div
                          style={{ display: "inline-block" }}
                          whileTap={{ scale: 0.95 }}
                        >
                          <Button
                            className="red-hover"
                            onClick={() => onClickCancelInvite(invite)}
                          >
                            Cancel
                          </Button>
                        </motion.div>
                      </div>
                    )}
                  </div>
                </motion.div>
              ))}
            </motion.div>
          </TabPane>

          {/* <TabPane tab="Content" key="content" disabled>
            <motion.div
              className="activity-list bookings-list"
              variants={listVariants}
              initial="hidden"
              animate="visible"
            >
              {content.map((a: any) => {
                const now = dayjs();
                const due = a.dueDate
                  ? dayjs(a.dueDate)
                  : a.date
                  ? dayjs(a.date)
                  : null;
                const isOverdue = due ? due.isBefore(now) : false;
                const pillText = a.expiresAt
                  ? computeTimeLeft(a.expiresAt)
                  : a.timeLeft ||
                    (due ? computeTimeLeft(due.toISOString()) : "");

                return (
                  <motion.div
                    className="booking-row"
                    key={a.id}
                    variants={itemVariants}
                  >
                    <div className="booking-left">
                      <div className="booking-icon-box">
                        <CameraOutlined className="booking-icon" />
                      </div>
                      <div className="booking-info">
                        <div className="booking-title">{a?.venue?.name}</div>
                        <div className="booking-address">
                          {a?.venue?.addressDisplay}
                        </div>
                        <div className="booking-comp" style={{ marginTop: 6 }}>
                          {a.applicationDate &&
                            `Visit Date: ${dayjs(a.applicationDate.date).format(
                              "MMMM D, YYYY"
                            )}`}
                          {a.deliverables
                            ? `  \u00A0 Deliverables: ${a.deliverables}`
                            : null}
                        </div>
                      </div>
                    </div>

                    <div className="booking-right">
                      <div className="booking-datetime">
                        <div className="booking-date">
                          Due:{" "}
                          {due
                            ? dayjs(due).format("MMMM D, YYYY")
                            : formatLeft(a.details)}
                        </div>
                        <div
                          className={`application-left-pill ${
                            isOverdue ? "overdue" : ""
                          }`}
                        >
                          {isOverdue ? "Overdue" : pillText}
                        </div>
                      </div>

                      <div className="booking-actions">
                        <motion.div
                          style={{ display: "inline-block" }}
                          whileTap={{ scale: 0.95 }}
                        >
                          <Button
                            size="small"
                            className="chat-btn"
                            icon={<MessageOutlined />}
                            onClick={() => onClickMessage(a)}
                          >
                            Chat
                          </Button>
                        </motion.div>
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </motion.div>
          </TabPane> */}
        </Tabs>
      </div>
    </div>
  );
};

export default PendingActivities;
